import React, { useRef, useState } from 'react'
import { Form, Button, Modal, Card, Spinner, Badge, Row, Col } from 'react-bootstrap'
import { FaBuilding, FaShieldAlt, FaSlidersH, FaTimes, FaUser, FaUsers, FaKey } from 'react-icons/fa'
import CreatableSelect from 'react-select/creatable'
import PasswordSection from './password-section'
import { useAuth } from '../../auth/auth-context'
import { canAccessAccountSettings } from '../../auth/settings-access'
import useFetch from '../../_fetch'
import { currencies } from '../../_utils'
import AccountAccessCard from './account-access'
import ApiSettings from './api-settings'
import { Navigate, NavLink, useParams } from 'react-router-dom'
import './user-settings.css'

function stripCommonSubdomain(hostname: string) {
    return hostname.replace(/^www\d*\./i, '')
}

function cleanDomain(input: string) {
    try {
        const value = input.trim()
        const url = value.startsWith('http') ? value : `https://${value}`
        const parsed = new URL(url)
        return stripCommonSubdomain(parsed.hostname.toLowerCase())
    } catch {
        return null
    }
}

function isValidDomain(domain: string) {
    return /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(domain)
}

export function canViewAccountAccess(user: any) {
    return canAccessAccountSettings(user) && !!(user?.admin
        || user?.canManageMembers
        || (user?._auth?.type === 'email' && user?.accountAccessRole === 'manager')
        || (!user?.isAdmin && !user?._auth && !user?.switchedFrom?.username))
}

export default function UserSettings() {
    const { section } = useParams<{ section: string }>()
    const { user, login } = useAuth()
    const { doFetch } = useFetch()
    const currency = user?.settings?.currency
    const companySettings = user?.settings?.company

    const [formData, setFormData] = useState({
        currency: currency || 'EUR',
        companyName: companySettings?.name || user?.companyName || '',
        companyContactEmail: companySettings?.contactEmail || user?.email || '',
        companyVatNumber: companySettings?.vatNumber || '',
        companyDomains: Array.isArray(companySettings?.domains) ? companySettings?.domains : []
    })
    const [submitting, setSubmitting] = useState(false)
    const [domainError, setDomainError] = useState('')
    const [companyNameError, setCompanyNameError] = useState('')
    const [companyDomainsError, setCompanyDomainsError] = useState('')

    const [successModalMsg, setSuccessModalMsg] = useState('')
    const [errorModalMsg, setErrorModalMsg] = useState('')
    const domainsSelectRef = useRef<any>(null)
    const actionError = domainError || companyNameError || companyDomainsError
    const isDomainFieldInvalid = !!(domainError || companyDomainsError)

    const domainSelectStyles = {
        control: (base: any, state: any) => ({
            ...base,
            borderColor: isDomainFieldInvalid ? '#dc3545' : base.borderColor,
            boxShadow: isDomainFieldInvalid
                ? '0 0 0 0.2rem rgba(220,53,69,0.25)'
                : state.isFocused
                    ? base.boxShadow
                    : 'none',
            '&:hover': {
                ...base['&:hover'],
                borderColor: isDomainFieldInvalid ? '#dc3545' : base.borderColor,
            },
        }),
    }

    const handleInputChange = (e: React.ChangeEvent<any>) => {
        const { name, value } = e.target

        if (name === 'companyName') {
            setCompanyNameError('')
        }

        setFormData((prevData) => ({
            ...prevData,
            [name]: value
        }))
    }

    const addDomain = (option: any) => {
        const raw = option?.value?.trim()?.toLowerCase()
        if (!raw) return

        const domain = cleanDomain(raw)
        if (!domain || !isValidDomain(domain)) {
            setDomainError('Invalid domain')
            domainsSelectRef.current?.focus()
            return
        }

        setDomainError('')
        setCompanyDomainsError('')
        setFormData((prevData) => ({
            ...prevData,
            companyDomains: (prevData.companyDomains || []).includes(domain)
                ? prevData.companyDomains
                : [...(prevData.companyDomains || []), domain]
        }))
    }

    const removeDomain = (domainToRemove: string) => {
        setFormData((prevData) => ({
            ...prevData,
            companyDomains: (prevData.companyDomains || []).filter((domain) => domain !== domainToRemove)
        }))
    }

    const onSettingsSubmit = async (e:React.FormEvent) => {
        e.preventDefault()

        setDomainError('')
        setCompanyNameError('')
        setCompanyDomainsError('')

        if (!formData.companyName.trim()) {
            setCompanyNameError('Company name is required')
            return
        }

        if (!(formData.companyDomains || []).length) {
            setCompanyDomainsError('At least one website domain is required')
            domainsSelectRef.current?.focus()
            return
        }

        setSubmitting(true)

        try {
            const res = await doFetch('/update-settings', {
                currency: formData.currency,
                companyName: formData.companyName,
                companyContactEmail: formData.companyContactEmail,
                companyVatNumber: formData.companyVatNumber,
                companyDomains: formData.companyDomains
            })
            
            if(res.status >= 400) throw await res.text()

            const {token} = await res.json()

            login(token)
            setSuccessModalMsg('Settings updated successfully')
        } catch (error) {
            setErrorModalMsg('Failed to update settings')
        } finally {
            setSubmitting(false)
        }
    }

    const showAccountAccess = canViewAccountAccess(user)
    const hasSettingsAccess = canAccessAccountSettings(user)
    const accountName = user?.username || 'Account'
    const identityEmail = user?._auth?.type === 'email' ? user._auth.email : ''
    const signedInAs = identityEmail || user?.switchedFrom?.username || user?.admin?.username || user?.username || '-'
    const accountType = user?.kind ? `${user.kind} account` : 'Account'
    const activeSection = section === 'account' ? 'general' : section
    const isValidSection = section === 'account' || section === 'access' || section === 'security' || section === 'api'

    if (!isValidSection
        || (!hasSettingsAccess && activeSection !== 'security')
        || (activeSection === 'access' && !showAccountAccess)) {
        return <Navigate to={hasSettingsAccess ? '/user-settings/account' : '/user-settings/security'} replace />
    }

    return (
        <div className="container-fluid px-3 user-settings-page">
            <Card className="user-settings-hero border-0 mb-4">
                <Card.Body className="d-flex flex-column flex-lg-row justify-content-between gap-3 align-items-lg-center">
                    <div>
                        <div className="user-settings-eyebrow">Settings</div>
                        <h4 className="mb-1">User settings</h4>
                        <div className="text-muted">Account-level and personal settings are separated below.</div>
                    </div>
                    <div className="user-settings-context-strip" aria-label="Current settings context">
                        <div className="user-settings-context-item is-account">
                            <span className="user-settings-context-icon"><FaBuilding aria-hidden="true" /></span>
                            <span className="min-w-0">
                                <small>Active {accountType}</small>
                                <strong className="text-truncate" title={accountName}>{accountName}</strong>
                            </span>
                        </div>
                        <div className="user-settings-context-item is-personal">
                            <span className="user-settings-context-icon"><FaUser aria-hidden="true" /></span>
                            <span className="min-w-0">
                                <small>Signed in as</small>
                                <strong className="text-truncate" title={signedInAs}>{signedInAs}</strong>
                            </span>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <Row className="g-4 align-items-start">
                <Col lg={4} xl={3}>
                    <Card className="user-settings-panel user-settings-sidebar border-0">
                        <Card.Header className="bg-white border-0">
                            <div className="user-settings-eyebrow">Configuration</div>
                            <h5 className="mb-0">Settings</h5>
                        </Card.Header>
                        <Card.Body className="pt-2">
                            <div className="user-settings-nav" role="navigation" aria-label="Account settings sections">
                                {hasSettingsAccess && (
                                    <>
                                        <div className="user-settings-nav-group-label">Account · {accountName}</div>
                                        <NavLink to="/user-settings/api" className={({ isActive }) => `user-settings-nav-item ${isActive ? 'is-active' : ''}`}>
                                            <span className="user-settings-nav-icon"><FaKey aria-hidden="true" /></span>
                                            <span><strong>API</strong><small>Keys and documentation</small></span>
                                        </NavLink>
                                        <NavLink
                                            to="/user-settings/account"
                                            className={({ isActive }) => `user-settings-nav-item ${isActive ? 'is-active' : ''}`}
                                        >
                                            <span className="user-settings-nav-icon"><FaSlidersH aria-hidden="true" /></span>
                                            <span><strong>Account profile</strong><small>Company and reporting</small></span>
                                        </NavLink>
                                        {showAccountAccess && (
                                            <NavLink
                                                to="/user-settings/access"
                                                className={({ isActive }) => `user-settings-nav-item ${isActive ? 'is-active' : ''}`}
                                            >
                                                <span className="user-settings-nav-icon"><FaUsers aria-hidden="true" /></span>
                                                <span><strong>Account access</strong><small>People and login policy</small></span>
                                            </NavLink>
                                        )}
                                    </>
                                )}
                                <div className="user-settings-nav-group-label user-settings-nav-personal-label">
                                    {identityEmail ? `Personal · ${identityEmail}` : 'Sign-in'}
                                </div>
                                <NavLink
                                    to="/user-settings/security"
                                    className={({ isActive }) => `user-settings-nav-item ${isActive ? 'is-active' : ''}`}
                                >
                                    <span className="user-settings-nav-icon"><FaShieldAlt aria-hidden="true" /></span>
                                    <span><strong>{identityEmail ? 'Personal security' : 'Security'}</strong><small>Password settings</small></span>
                                </NavLink>
                            </div>

                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={8} xl={9}>
                    {activeSection === 'general' && (
                        <Form onSubmit={onSettingsSubmit}>
                            <Card className="user-settings-panel border-0">
                                <Card.Header className="bg-white border-0 user-settings-card-header">
                                    <div className="user-settings-eyebrow text-capitalize">{accountType}</div>
                                    <h5 className="mb-1">Account profile</h5>
                                    <div className="text-muted small">These settings belong to <strong>{accountName}</strong>{identityEmail ? `, not your personal login ${identityEmail}` : ''}.</div>
                                </Card.Header>
                                <Card.Body>
                                    <div className="user-settings-identity-grid">
                                        <div>
                                            <div className="user-settings-field-label">Account name</div>
                                            <div className="fw-semibold">{user?.username || '-'}</div>
                                            <div className="text-muted small">Used to identify this account.</div>
                                        </div>
                                        <div>
                                            <div className="user-settings-field-label">Account type</div>
                                            <div className="fw-semibold text-capitalize">{user?.kind || '-'}</div>
                                            <div className="text-muted small">Controls the available reporting features.</div>
                                        </div>
                                    </div>

                                    <div className="user-settings-section-divider" />

                                    <div className="user-settings-section-heading">
                                        <h6>Reporting preferences</h6>
                                        <p>Choose the default currency used across dashboards and reports.</p>
                                    </div>
                                    <Row className="g-4">
                                        <Col md={6}>
                                            <Form.Group controlId="formCurrency">
                                                <Form.Label>Currency</Form.Label>
                                                <Form.Select
                                                    name="currency"
                                                    value={formData.currency}
                                                    onChange={handleInputChange}
                                                >
                                                    {currencies.map((currency) => (
                                                        <option key={currency} value={currency}>{currency}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <div className="user-settings-section-divider" />

                                    <div className="user-settings-section-heading">
                                        <h6>Company details</h6>
                                        <p>Keep the business details associated with this account up to date.</p>
                                    </div>
                                    <Row className="g-4">
                                        <Col md={6}>
                                            <Form.Group controlId="formCompanyName">
                                                <Form.Label>Company name</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="companyName"
                                                    value={formData.companyName}
                                                    onChange={handleInputChange}
                                                    placeholder="Company name"
                                                    isInvalid={!!companyNameError}
                                                />
                                                <Form.Control.Feedback type="invalid">{companyNameError}</Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group controlId="formCompanyEmail">
                                                <Form.Label>Contact email <span className="text-muted fw-normal">(optional)</span></Form.Label>
                                                <Form.Control
                                                    type="email"
                                                    name="companyContactEmail"
                                                    value={formData.companyContactEmail}
                                                    onChange={handleInputChange}
                                                    placeholder="name@company.com"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group controlId="formCompanyVat">
                                                <Form.Label>VAT / CVR <span className="text-muted fw-normal">(optional)</span></Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="companyVatNumber"
                                                    value={formData.companyVatNumber}
                                                    onChange={handleInputChange}
                                                    placeholder="VAT or registration number"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group controlId="formCompanyDomains">
                                                <Form.Label>Website domains</Form.Label>
                                                <CreatableSelect
                                                    ref={domainsSelectRef}
                                                    placeholder="Add domain (e.g. partner.com)"
                                                    isClearable
                                                    styles={domainSelectStyles}
                                                    formatCreateLabel={(value) => `Add “${value}”`}
                                                    onChange={addDomain}
                                                    onCreateOption={(value) => addDomain({ value })}
                                                />
                                                <div className="d-flex flex-wrap gap-2 mt-2">
                                                    {(formData.companyDomains || []).map((domain) => (
                                                        <Badge key={domain} bg="light" text="dark" className="user-settings-domain-badge">
                                                            {domain}
                                                            <button
                                                                type="button"
                                                                aria-label={`Remove ${domain}`}
                                                                onClick={() => removeDomain(domain)}
                                                            >
                                                                <FaTimes aria-hidden="true" />
                                                            </button>
                                                        </Badge>
                                                    ))}
                                                </div>
                                                {isDomainFieldInvalid && <div className="text-danger small mt-1">{domainError || companyDomainsError}</div>}
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card.Body>
                                <Card.Footer className="user-settings-card-footer d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3">
                                    <div className={`small ${actionError ? 'text-danger' : 'text-muted'}`} aria-live="polite">
                                        {actionError || `Changes apply to ${accountName} only.`}
                                    </div>
                                    <Button variant="primary" type="submit" disabled={submitting}>
                                        {submitting ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Saving…</> : 'Save changes'}
                                    </Button>
                                </Card.Footer>
                            </Card>
                        </Form>
                    )}

                    {activeSection === 'access' && showAccountAccess && <AccountAccessCard accountName={accountName} />}
                    {activeSection === 'api' && <ApiSettings />}
                    {activeSection === 'security' && <PasswordSection setSuccessModalMsg={setSuccessModalMsg} identityLabel={signedInAs} isEmailIdentity={!!identityEmail} />}
                </Col>
            </Row>

            <Modal show={!!successModalMsg} onHide={() => setSuccessModalMsg('')}>
                <Modal.Header closeButton className="bg-success text-white">
                    <Modal.Title>Success</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{successModalMsg}</p>
                </Modal.Body>
            </Modal>

            <Modal show={!!errorModalMsg} onHide={() => setErrorModalMsg('')}>
                <Modal.Header closeButton className="bg-danger text-white">
                    <Modal.Title>Error</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{errorModalMsg}</p>
                </Modal.Body>
            </Modal>
        </div>
    )
}
